import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  IconButton
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, Refresh } from '@mui/icons-material';
import { format } from 'date-fns';
import { parseTimeEntryDate, formatTimeEntry } from '../utils/dateUtils';
import { timeService } from '../services/timeService';
import { employeeService } from '../services/employeeService';
import { useAuth } from '../hooks/useAuth';

const REFRESH_MS = 60_000;

function employeeFullName(row) {
  return [row?.employee_first_name, row?.employee_last_name].filter(Boolean).join(' ') || '—';
}

function isDataCollectionEmployee(employee) {
  return employee?.data_collection === true;
}

function formatPunchTime(v) {
  return formatTimeEntry(v, 'h:mm a');
}

function roundHoursFromMs(ms) {
  return Math.round((ms / (1000 * 60 * 60)) * 100) / 100;
}

function getPunchHours(punch, now = new Date()) {
  if (punch.clock_out != null) {
    if (punch.hours_worked != null) return Number(punch.hours_worked) || 0;
    const clockIn = parseTimeEntryDate(punch.clock_in);
    const clockOut = parseTimeEntryDate(punch.clock_out);
    if (clockIn && clockOut) return roundHoursFromMs(clockOut.getTime() - clockIn.getTime());
    return 0;
  }
  const clockIn = parseTimeEntryDate(punch.clock_in);
  if (!clockIn) return 0;
  return roundHoursFromMs(now.getTime() - clockIn.getTime());
}

function getGroupTotalHours(punches, now = new Date()) {
  return punches.reduce((sum, punch) => sum + getPunchHours(punch, now), 0);
}

function formatEmployeePunchSummary(punches, now = new Date()) {
  const count = punches.length;
  if (count === 0) return 'No punches today';
  const totalHours = getGroupTotalHours(punches, now);
  return `${count} punch${count === 1 ? '' : 'es'} - ${totalHours.toFixed(2)} hr`;
}

/** Sort key: clocked in → no punches (absent) → clocked out, then name. */
function attendanceSortRank(group) {
  if (group.isClockedIn) return 0;
  if (!group.punches?.length) return 1;
  return 2;
}

function groupEntriesByEmployee(entries, dataCollectionEmployees = []) {
  const map = new Map();
  for (const entry of entries) {
    const id = entry.employee_id;
    if (id == null) continue;
    if (!map.has(id)) {
      map.set(id, {
        employee_id: id,
        employee_number: entry.employee_number,
        employee_first_name: entry.employee_first_name,
        employee_last_name: entry.employee_last_name,
        punches: []
      });
    }
    map.get(id).punches.push(entry);
  }

  // Include Data Collection employees even with zero punches today
  for (const emp of dataCollectionEmployees) {
    if (emp?.id == null || map.has(emp.id)) continue;
    map.set(emp.id, {
      employee_id: emp.id,
      employee_number: emp.employee_number,
      employee_first_name: emp.first_name,
      employee_last_name: emp.last_name,
      punches: []
    });
  }

  const groups = [...map.values()];
  for (const group of groups) {
    group.punches.sort((a, b) => {
      const ta = parseTimeEntryDate(a.clock_in)?.getTime() ?? 0;
      const tb = parseTimeEntryDate(b.clock_in)?.getTime() ?? 0;
      return ta - tb;
    });
    group.isClockedIn = group.punches.some((p) => p.clock_out == null);
    group.hasNoPunches = group.punches.length === 0;
  }

  groups.sort((a, b) => {
    const rankDiff = attendanceSortRank(a) - attendanceSortRank(b);
    if (rankDiff !== 0) return rankDiff;
    return employeeFullName(a).localeCompare(employeeFullName(b));
  });

  return groups;
}

function statusChipProps(group) {
  if (group.isClockedIn) {
    return { label: 'Clocked in', color: 'success', variant: 'filled' };
  }
  if (group.hasNoPunches) {
    return { label: 'No punches', color: 'warning', variant: 'outlined' };
  }
  return { label: 'Clocked out', color: 'default', variant: 'outlined' };
}

const headerRowSx = (group) => {
  let bgcolor = 'grey.50';
  let hoverBg = 'grey.100';
  if (group.isClockedIn) {
    bgcolor = 'rgba(76, 175, 80, 0.08)';
    hoverBg = 'rgba(76, 175, 80, 0.14)';
  } else if (group.hasNoPunches) {
    bgcolor = 'rgba(255, 152, 0, 0.06)';
    hoverBg = 'rgba(255, 152, 0, 0.12)';
  }
  return {
    bgcolor,
    cursor: 'pointer',
    '&:hover': { bgcolor: hoverBg },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid',
      borderColor: 'divider',
      py: 0.75,
      verticalAlign: 'middle'
    }
  };
};

const COL_STATUS = '18%';
const COL_NAME = '28%';
const COL_DETAIL = '54%';
const EXPANDED_BLOCK_WIDTH = '72%';
const EXPANDED_BLOCK_MAX = 720;

const expandedRowBg = (group) => {
  if (group.isClockedIn) return 'rgba(76, 175, 80, 0.08)';
  if (group.hasNoPunches) return 'rgba(255, 152, 0, 0.06)';
  return 'grey.50';
};

function ExpandedBlock({ children, sx }) {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        ...sx
      }}
    >
      <Box sx={{ width: EXPANDED_BLOCK_WIDTH, maxWidth: EXPANDED_BLOCK_MAX }}>{children}</Box>
    </Box>
  );
}

function EmployeePunchGroup({ group, now }) {
  const [open, setOpen] = useState(false);
  const rowSx = headerRowSx(group);
  const chip = statusChipProps(group);

  const toggle = () => setOpen((prev) => !prev);

  return (
    <>
      <TableRow
        onClick={toggle}
        sx={{
          ...rowSx,
          '& .MuiTableCell-root': rowSx['& .MuiTableCell-root']
        }}
      >
        <TableCell colSpan={3} sx={{ py: 0.75, verticalAlign: 'middle' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <Box sx={{ width: COL_STATUS, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  aria-label={open ? 'Collapse punches' : 'Expand punches'}
                  aria-expanded={open}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle();
                  }}
                  sx={{ p: 0.25, flexShrink: 0 }}
                >
                  {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                </IconButton>
                <Chip
                  size="small"
                  label={chip.label}
                  color={chip.color}
                  variant={chip.variant}
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              </Box>
              <Box
                sx={{
                  width: COL_NAME,
                  flexShrink: 0,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  flexWrap: 'nowrap'
                }}
              >
                <Typography
                  variant="subtitle2"
                  component="span"
                  sx={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0
                  }}
                >
                  {employeeFullName(group)}
                </Typography>
                {group.employee_number != null && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="span"
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    #{group.employee_number}
                  </Typography>
                )}
              </Box>
            </Box>
            <ExpandedBlock
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'none'
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                component="span"
                sx={{ display: 'block', width: '100%', textAlign: 'right' }}
              >
                {formatEmployeePunchSummary(group.punches, now)}
              </Typography>
            </ExpandedBlock>
          </Box>
        </TableCell>
      </TableRow>
      {open ? (
        <TableRow>
          <TableCell
            colSpan={3}
            sx={{ py: 0, border: 0, bgcolor: expandedRowBg(group) }}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <ExpandedBlock sx={{ pb: 0.75, pt: 0.25 }}>
                {group.hasNoPunches ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, textAlign: 'center' }}>
                    No punches recorded today.
                  </Typography>
                ) : (
                  <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableBody>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600, width: '24%' }}>Clock in</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '24%' }}>Clock out</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '14%' }} align="right">
                          Hours
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Tasks</TableCell>
                      </TableRow>
                      {group.punches.map((punch, index) => (
                        <TableRow
                          key={punch.id}
                          sx={{
                            '& .MuiTableCell-root': {
                              borderBottom:
                                index === group.punches.length - 1 ? '1px solid' : undefined,
                              borderColor: 'divider'
                            }
                          }}
                        >
                          <TableCell sx={{ width: '24%' }}>{formatPunchTime(punch.clock_in)}</TableCell>
                          <TableCell sx={{ width: '24%' }}>
                            {punch.clock_out ? formatPunchTime(punch.clock_out) : '—'}
                          </TableCell>
                          <TableCell sx={{ width: '14%' }} align="right">
                            {getPunchHours(punch, now).toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                            {punch.clock_out
                              ? punch.task_summary || '—'
                              : <>Now: <strong>{punch.current_task_label || '—'}</strong></>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </ExpandedBlock>
            </Collapse>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

const ClockedInToday = () => {
  const { isManagerOrAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [dataCollectionEmployees, setDataCollectionEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [now, setNow] = useState(() => new Date());

  const todayYmd = format(new Date(), 'yyyy-MM-dd');
  const todayLabel = format(new Date(), 'EEEE, MMMM d, yyyy');

  const loadToday = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const timePromise = timeService.getAll({
        dateFrom: todayYmd,
        dateTo: todayYmd,
        limit: 500
      });
      const empPromise = employeeService
        .getAll({ activeOnly: true, dataCollectionOnly: true, limit: 500 })
        .catch((err) => {
          console.error('Error loading Data Collection roster:', err);
          return { data: [] };
        });

      const [timeRes, empRes] = await Promise.all([timePromise, empPromise]);
      const list = timeRes.data ?? [];
      setEntries(Array.isArray(list) ? list : []);
      const employees = empRes.data ?? [];
      setDataCollectionEmployees(
        Array.isArray(employees) ? employees.filter(isDataCollectionEmployee) : []
      );
      setLastLoadedAt(new Date());
    } catch (err) {
      console.error('Error loading today punches:', err);
      setError(err.response?.data?.message || 'Failed to load today\'s time punches');
      setEntries([]);
      setDataCollectionEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [todayYmd]);

  useEffect(() => {
    loadToday();
  }, [loadToday]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(loadToday, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [loadToday]);

  const groups = useMemo(
    () => groupEntriesByEmployee(entries, dataCollectionEmployees),
    [entries, dataCollectionEmployees]
  );
  const clockedInCount = groups.filter((g) => g.isClockedIn).length;
  const withPunchesCount = groups.filter((g) => !g.hasNoPunches).length;
  const noPunchesCount = groups.filter((g) => g.hasNoPunches).length;

  if (!isManagerOrAdmin()) {
    return (
      <Box>
        <Alert severity="warning">You do not have permission to view who is clocked in.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            Clocked In
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            All Data Collection employees for {todayLabel}, including those with no punches. Refreshes every minute.
          </Typography>
          {!loading && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {groups.length} Data Collection employee{groups.length === 1 ? '' : 's'}
              {' · '}
              {withPunchesCount} with punches today
              {' · '}
              {clockedInCount} currently clocked in
              {noPunchesCount > 0 ? ` · ${noPunchesCount} with no punches` : ''}
              {lastLoadedAt ? ` · Updated ${format(lastLoadedAt, 'h:mm a')}` : ''}
            </Typography>
          )}
        </Box>
        <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadToday} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !groups.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : !groups.length ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No Data Collection employees found.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ width: '80%', mx: 'auto' }}>
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableBody>
                  {groups.map((group) => (
                    <EmployeePunchGroup key={group.employee_id} group={group} now={now} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ClockedInToday;
